import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Mail, RefreshCcw, User as UserIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getChannelPlans,
  confirmManualRenewal,
  type ChannelPlanStatus,
} from '@/services/channelPlanService';
import { fetchUsers } from '@/services/authService';

const PAGE_SIZE = 20;

const STATUS_PILL_CLASSES: Record<ChannelPlanStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  GRACE: 'bg-yellow-100 text-yellow-800',
  SUSPENDED: 'bg-red-100 text-red-800',
};

const RENEWABLE_STATUSES: ChannelPlanStatus[] = ['GRACE', 'SUSPENDED'];

const ChannelPlansStatus = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ChannelPlanStatus | ''>('');
  const [renewingId, setRenewingId] = useState<number | null>(null);

  const {
    data: channelPlansPage,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['channelPlans', page, statusFilter],
    queryFn: () =>
      getChannelPlans({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter || undefined,
      }),
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
  });

  const usersById = useMemo(() => {
    const map = new Map<string, { name: string | null; email: string }>();
    users?.forEach((user) => {
      map.set(user.id, { name: user.name, email: user.email });
    });
    return map;
  }, [users]);

  const { mutate: renewChannelPlan } = useMutation({
    mutationFn: (channelPlanId: number) => confirmManualRenewal(channelPlanId),
    onMutate: (channelPlanId: number) => {
      setRenewingId(channelPlanId);
    },
    onSuccess: () => {
      toast.success(t('channelPlanRenewed') || 'Channel plan renewed');
      queryClient.invalidateQueries({ queryKey: ['channelPlans'] });
    },
    onError: (err: unknown) => {
      const apiError = err as { response?: { data?: { message?: string } }; message?: string };
      const msg =
        apiError?.response?.data?.message ||
        apiError?.message ||
        t('errorRenewingChannelPlan') ||
        'Error renewing channel plan';
      toast.error(msg);
    },
    onSettled: () => {
      setRenewingId(null);
    },
  });

  const handleRenew = (channelPlanId: number) => {
    const confirmed = window.confirm(
      t('confirmRenewChannelPlan') || 'Are you sure you want to renew this channel plan?'
    );
    if (!confirmed) return;
    renewChannelPlan(channelPlanId);
  };

  const items = channelPlansPage?.items ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">{t('loadingChannelPlans')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-red-500">{t('errorLoadingChannelPlans')}</p>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('channelPlansStatusTitle')}</h1>
        <p className="text-sm text-gray-600">{t('channelPlansStatusDescription')}</p>
      </div>

      <div className="flex items-center gap-2">
        <label htmlFor="channel-plan-status-filter" className="text-sm font-medium text-gray-700">
          {t('filterByStatus')}
        </label>
        <select
          id="channel-plan-status-filter"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ChannelPlanStatus | '');
            setPage(1);
          }}
        >
          <option value="">{t('allStatuses')}</option>
          <option value="ACTIVE">{t('channelPlanStatusActive')}</option>
          <option value="GRACE">{t('channelPlanStatusGrace')}</option>
          <option value="SUSPENDED">{t('channelPlanStatusSuspended')}</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('userName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('channels')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('plan')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('endDate')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {items.map((row) => {
                const user = usersById.get(row.userId);
                const canRenew = RENEWABLE_STATUSES.includes(row.status);
                return (
                  <tr key={row.id} className="transition hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                          <UserIcon className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user?.name || t('noName') || row.userId}
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <Mail className="mr-1 h-3 w-3 text-gray-400" />
                            {user?.email || t('notAvailable')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {row.channel?.nameChannel || t('notAvailable')}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {row.plan.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${STATUS_PILL_CLASSES[row.status]}`}
                      >
                        {t(
                          row.status === 'ACTIVE'
                            ? 'channelPlanStatusActive'
                            : row.status === 'GRACE'
                              ? 'channelPlanStatusGrace'
                              : 'channelPlanStatusSuspended'
                        )}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {row.endDate ? new Date(row.endDate).toLocaleDateString() : t('notAvailable')}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      {canRenew && (
                        <button
                          onClick={() => handleRenew(row.id)}
                          disabled={renewingId === row.id}
                          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <RefreshCcw className="h-4 w-4" />
                          {t('renewChannelPlan')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {items.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-gray-500">{t('noChannelPlansFound')}</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('previous')}
        </button>
        <span className="text-sm text-gray-600">
          {t('page')} {page}
        </span>
        <button
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          disabled={items.length < PAGE_SIZE}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('next')}
        </button>
      </div>
    </section>
  );
};

export default ChannelPlansStatus;
